import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Plus,
  Upload,
  Download,
  Edit,
  Trash2,
  FolderOpen,
  FileText,
  History
} from 'lucide-react';
import { 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Card, 
  message,
  Space,
  Tooltip,
  DatePicker,
  Row,
  Col,
  Dropdown,
  Menu,
  Upload as AntUpload,
  Radio
} from 'antd';
import DashboardLayout from '../components/layout/DashboardLayout';
import CustomTable from '../components/common/CustomTable';
import api from '../config/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const Accounts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  
  // Modal states
  const [addTransactionModal, setAddTransactionModal] = useState(false);
  const [editTransactionModal, setEditTransactionModal] = useState(false);
  const [newSessionModal, setNewSessionModal] = useState(false);
  const [importExcelModal, setImportExcelModal] = useState(false);
  const [sessionsModal, setSessionsModal] = useState(false);
  
  // Selected transaction for editing
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Form instances
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [sessionForm] = Form.useForm();
  const [importForm] = Form.useForm();

  useEffect(() => {
    fetchSessions();
    fetchActiveSession();
  }, []);

  useEffect(() => {
    if (activeSession) {
      fetchTransactions();
    }
  }, [activeSession]);

  const fetchSessions = async () => {
    try {
      const response = await api.get('/sessions');
      if (response.data.success) {
        setSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchActiveSession = async () => {
    try {
      const response = await api.get('/sessions/active');
      if (response.data.success) {
        setActiveSession(response.data.session);
      }
    } catch (error) {
      console.error('No active session found');
    }
  };

  const fetchTransactions = async () => {
    if (!activeSession) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/transactions?sessionId=${activeSession._id}`);
      if (response.data.success) {
        setTransactions(response.data.transactions);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      message.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (values) => {
    try {
      setLoading(true);
      const response = await api.post('/sessions', values);
      if (response.data.success) {
        message.success('Session created successfully!');
        setActiveSession(response.data.session);
        fetchSessions();
        setNewSessionModal(false);
        sessionForm.resetFields();
      }
    } catch (error) {
      console.error('Error creating session:', error);
      message.error('Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchSession = async (sessionId) => {
    try {
      setLoading(true);
      const response = await api.put(`/sessions/${sessionId}/activate`);
      if (response.data.success) {
        setActiveSession(response.data.session);
        message.success('Session switched successfully!');
        setSessionsModal(false);
      }
    } catch (error) {
      console.error('Error switching session:', error);
      message.error('Failed to switch session');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async (values) => {
    if (!activeSession) {
      message.error('Please select or create a session first');
      return;
    }
  
    try {
      setLoading(true);
      
      // Get the amounts (either from auto-calculation or manual entry)
      let debit = parseFloat(values.debitAmount || 0);
      let credit = parseFloat(values.creditAmount || 0);
      
      // If both are zero, calculate from qty and rate based on type
      if (debit === 0 && credit === 0) {
        const qty = parseFloat(values.quantity || 0);
        const rate = parseFloat(values.rate || 0);
        const calculatedAmount = qty * rate;
        
        if (values.amountType === 'debit') {
          debit = calculatedAmount;
        } else if (values.amountType === 'credit') {
          credit = calculatedAmount;
        }
      }
      
      const transactionData = {
        sessionId: activeSession._id,
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        quantity: parseFloat(values.quantity || 1),
        rate: parseFloat(values.rate || 0),
        transport: values.transport || '',
        debitAmount: debit,
        creditAmount: credit
      };
  
      const response = await api.post('/transactions', transactionData);
      if (response.data.success) {
        message.success('Transaction added successfully!');
        fetchTransactions();
        fetchActiveSession();
        setAddTransactionModal(false);
        addForm.resetFields();
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      message.error(error.response?.data?.message || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTransaction = async (values) => {
    if (!selectedTransaction) return;

    try {
      setLoading(true);
      const response = await api.put(`/transactions/${selectedTransaction._id}`, {
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        quantity: parseFloat(values.quantity || 1),
        rate: parseFloat(values.rate || 0),
        transport: values.transport || '',
        debitAmount: parseFloat(values.debitAmount || 0),
        creditAmount: parseFloat(values.creditAmount || 0)
      });

      if (response.data.success) {
        message.success('Transaction updated successfully!');
        fetchTransactions();
        fetchActiveSession();
        setEditTransactionModal(false);
        setSelectedTransaction(null);
        editForm.resetFields();
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      message.error(error.response?.data?.message || 'Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    Modal.confirm({
      title: 'Delete Transaction',
      content: 'Are you sure you want to delete this transaction? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.delete(`/transactions/${transactionId}`);
          if (response.data.success) {
            message.success('Transaction deleted successfully!');
            fetchTransactions();
            fetchActiveSession();
          }
        } catch (error) {
          console.error('Error deleting transaction:', error);
          message.error(error.response?.data?.message || 'Failed to delete transaction');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleImportExcel = async (values) => {
    if (!activeSession) {
      message.error('Please select or create a session first');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', values.file.file);
      formData.append('importMode', values.importMode);

      const response = await api.post(`/sessions/${activeSession._id}/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        message.success(`Imported ${response.data.imported} transactions successfully!`);
        if (response.data.errors && response.data.errors.length > 0) {
          message.warning(`Some rows had errors: ${response.data.errors.join(', ')}`);
        }
        fetchTransactions();
        fetchActiveSession();
        setImportExcelModal(false);
        importForm.resetFields();
      }
    } catch (error) {
      console.error('Error importing Excel:', error);
      message.error(error.response?.data?.message || 'Failed to import Excel file');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!activeSession) {
      message.error('Please select or create a session first');
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/transactions/export-pdf/${activeSession._id}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeSession.sessionName}_statement.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

// In Accounts.js - Update the transactionColumns array
const transactionColumns = [
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 80,
    // Remove: fixed: 'left',
    sorter: true,
    render: (date) => (
      <div className="text-xs font-medium">
        {dayjs(date).format('DD/MM/YY')}
      </div>
    )
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    width: 180,
    ellipsis: true,
    render: (text) => (
      <Tooltip title={text}>
        <div className="text-xs">{text}</div>
      </Tooltip>
    )
  },
  {
    title: 'Qty',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 50,
    align: 'center',
    // Remove: responsive: ['sm'],
    render: (qty) => <span className="text-xs font-medium">{qty}</span>
  },
  {
    title: 'Rate',
    dataIndex: 'rate',
    key: 'rate',
    width: 80,
    align: 'right',
    // Remove: responsive: ['md'],
    render: (rate) => (
      <span className="text-xs font-medium">
        {rate ? `₹${rate.toLocaleString()}` : '-'}
      </span>
    )
  },
  {
    title: 'Transport',
    dataIndex: 'transport',
    key: 'transport',
    width: 80,
    // Remove: responsive: ['lg'],
    render: (transport) => (
      <span className="text-xs">{transport || '-'}</span>
    )
  },
  {
    title: 'Debit',
    dataIndex: 'debitAmount',
    key: 'debitAmount',
    width: 90,
    align: 'right',
    render: (amount) => amount > 0 ? (
      <span className="text-red-600 font-bold text-xs">
        ₹{amount.toLocaleString()}
      </span>
    ) : (
      <span className="text-xs text-gray-400">-</span>
    )
  },
  {
    title: 'Credit',
    dataIndex: 'creditAmount',
    key: 'creditAmount',
    width: 90,
    align: 'right',
    render: (amount) => amount > 0 ? (
      <span className="text-green-600 font-bold text-xs">
        ₹{amount.toLocaleString()}
      </span>
    ) : (
      <span className="text-xs text-gray-400">-</span>
    )
  },
  {
    title: 'Balance',
    dataIndex: 'balance',
    key: 'balance',
    width: 90,
    align: 'right',
    // Remove: fixed: 'right',
    render: (balance) => (
      <span className={`font-bold text-xs ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        ₹{Math.abs(balance).toLocaleString()}
      </span>
    )
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 80,
    // Remove: fixed: 'right',
    render: (_, record) => (
      <Space size={2}>
        <Tooltip title="Edit">
          <Button 
            type="text" 
            size="small" 
            icon={<Edit size={12} />}
            className="!p-1 !min-w-0"
            onClick={() => {
              setSelectedTransaction(record);
              editForm.setFieldsValue({
                ...record,
                date: dayjs(record.date)
              });
              setEditTransactionModal(true);
            }}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <Button 
            type="text" 
            size="small"
            danger 
            icon={<Trash2 size={12} />}
            className="!p-1 !min-w-0"
            onClick={() => handleDeleteTransaction(record._id)}
          />
        </Tooltip>
      </Space>
    ),
  }
];

  const addAccountMenu = (
    <Menu>
      <Menu.Item key="form" icon={<Plus size={14} />} onClick={() => setAddTransactionModal(true)}>
        <span className="text-xs sm:text-sm">Add Transaction</span>
      </Menu.Item>
      <Menu.Item key="excel" icon={<Upload size={14} />} onClick={() => setImportExcelModal(true)}>
        <span className="text-xs sm:text-sm">Import Excel</span>
      </Menu.Item>
    </Menu>
  );

  const sessionMenu = (
    <Menu>
      <Menu.Item key="new" icon={<Plus size={14} />} onClick={() => setNewSessionModal(true)}>
        <span className="text-xs sm:text-sm">New Session</span>
      </Menu.Item>
      <Menu.Item key="switch" icon={<FolderOpen size={14} />} onClick={() => setSessionsModal(true)}>
        <span className="text-xs sm:text-sm">Switch Session</span>
      </Menu.Item>
    </Menu>
  );


const headerActions = [
  <Dropdown key="sessions" overlay={sessionMenu} trigger={['click']}>
    <Button icon={<History size={14} />} size="small">
      <span className="inline text-xs">Sessions</span>
    </Button>
  </Dropdown>,
  
  <Button 
    key="export"
    icon={<Download size={14} />} 
    type="default" 
    onClick={handleExportPDF} 
    loading={loading}
    size="small"
    className="md:inline-flex"
  >
    <span className="lg:inline text-xs">Export PDF</span>
  </Button>,
  
  <Dropdown key="add" overlay={addAccountMenu} trigger={['click']}>
    <Button type="primary" icon={<Plus size={14} />} size="small">
      <span className="inline text-xs">Add</span>
    </Button>
  </Dropdown>
];

  const breadcrumbs = [
    { title: 'Dashboard', onClick: () => navigate('/dashboard') },
    { title: 'Transactions' }
  ];


return (
  <DashboardLayout 
    title="Account Transactions" 
    subtitle={activeSession ? `Session: ${activeSession.sessionName}` : 'No active session'}
    headerActions={headerActions}
    breadcrumbs={breadcrumbs}
  >
    <div className="h-full flex flex-col p-4 gap-4">
      {/* Session Info Card - Fixed height */}
      {activeSession && (
        <Card size="small" className="bg-blue-50 border-blue-200 flex-shrink-0">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-blue-800 truncate">
                {activeSession.sessionName}
              </h3>
              <p className="text-xs text-blue-600 truncate">
                {activeSession.description}
              </p>
              <div className="text-xs text-blue-600 mt-2 grid grid-cols-2 sm:flex sm:gap-4">
                <span>Trans: {activeSession.totalTransactions}</span>
                <span>Bal: ₹{Math.abs(activeSession.currentBalance).toLocaleString()}</span>
                <span className="lg:inline col-span-2 sm:col-span-1">
                  Modified: {dayjs(activeSession.lastModified).format('DD/MM/YY HH:mm')}
                </span>
              </div>
            </div>
            <Button 
              onClick={() => setSessionsModal(true)} 
              icon={<FolderOpen size={14} />} 
              size="small"
              className="self-start lg:self-center"
            >
              <span className="inline text-xs">Switch</span>
            </Button>
          </div>
        </Card>
      )}

      {/* No Session Card */}
      {!activeSession && (
        <Card className="bg-yellow-50 border-yellow-200 text-center flex-shrink-0">
          <div className="py-6">
            <FileText className="w-12 h-12 mx-auto text-yellow-600 mb-3" />
            <h3 className="text-sm font-semibold text-yellow-800 mb-2">
              No Active Session
            </h3>
            <p className="text-xs text-yellow-600 mb-4">
              Create or switch to a session to start working.
            </p>
            <Space size="small" wrap>
              <Button 
                type="primary" 
                onClick={() => setNewSessionModal(true)} 
                icon={<Plus size={14} />} 
                size="small"
              >
                <span className="text-xs">Create Session</span>
              </Button>
              <Button 
                onClick={() => setSessionsModal(true)} 
                icon={<FolderOpen size={14} />} 
                size="small"
              >
                <span className="text-xs">Browse Sessions</span>
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {/* Transactions Table - This will be scrollable */}
      {activeSession && (
        <Card size="small" className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-hidden">
            <CustomTable
              columns={transactionColumns}
              dataSource={transactions}
              loading={loading}
              scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }} // Fixed height
              pagination={{
                pageSize: 25,
                showTotal: (total, range) => (
                  <span className="text-xs">
                    {window.innerWidth >= 640 
                      ? `${range[0]}-${range[1]} of ${total} transactions`
                      : `${total} total`
                    }
                  </span>
                )
              }}
            />
          </div>
        </Card>
      )}
    </div>
  

      {/* New Session Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Create New Session</span>}
        open={newSessionModal}
        onCancel={() => {
          setNewSessionModal(false);
          sessionForm.resetFields();
        }}
        footer={null}
        width="min(90vw, 500px)"
        centered
      >
        <Form
          form={sessionForm}
          layout="vertical"
          onFinish={handleCreateSession}
          className="mt-4"
        >
          <Form.Item
            name="sessionName"
            label={<span className="text-xs font-medium">Session Name</span>}
            rules={[{ required: true, message: 'Please enter session name' }]}
          >
            <Input placeholder="e.g., March 2025 Accounts" className="text-xs" />
          </Form.Item>

          <Form.Item 
            name="description" 
            label={<span className="text-xs font-medium">Description</span>}
          >
            <TextArea 
              rows={3} 
              placeholder="Optional description"
              maxLength={500}
              showCount
              className="text-xs"
            />
          </Form.Item>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              onClick={() => {
                setNewSessionModal(false);
                sessionForm.resetFields();
              }}
              size="small"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              <span className="text-xs">Create</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Sessions List Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Switch Session</span>}
        open={sessionsModal}
        onCancel={() => setSessionsModal(false)}
        footer={null}
        width="min(90vw, 700px)"
        centered
      >
        <div className="space-y-3 max-h-60 sm:max-h-80 overflow-y-auto">
          {sessions.map(session => (
            <Card 
              key={session._id}
              size="small"
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                session.isActive ? 'bg-blue-50 border-blue-200' : ''
              }`}
              onClick={() => handleSwitchSession(session._id)}
            >
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold truncate">{session.sessionName}</h4>
                  <p className="text-xs text-gray-600 truncate">{session.description}</p>
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-2">
                    <span>{session.totalTransactions} trans</span>
                    <span>₹{Math.abs(session.currentBalance).toLocaleString()}</span>
                    <span className=" inline">
                      {dayjs(session.lastModified).format('DD/MM/YY')}
                    </span>
                  </div>
                </div>
                {session.isActive && (
                  <span className="text-blue-600 font-medium text-xs flex-shrink-0">
                    Active
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      </Modal>

   {/* Add Transaction Modal */}
<Modal
  title={<span className="text-sm font-semibold">Add Transaction</span>}
  open={addTransactionModal}
  onCancel={() => {
    setAddTransactionModal(false);
    addForm.resetFields();
  }}
  footer={null}
  width="min(95vw, 800px)"
  centered
>
  <Form
    form={addForm}
    layout="vertical"
    onFinish={handleAddTransaction}
    className="mt-4"
    onValuesChange={(changedValues, allValues) => {
      // Auto-calculate amounts when quantity, rate, or type changes
      if (changedValues.quantity !== undefined || 
          changedValues.rate !== undefined || 
          changedValues.amountType !== undefined) {
        
        const qty = parseFloat(allValues.quantity || 0);
        const rate = parseFloat(allValues.rate || 0);
        const calculatedAmount = qty * rate;
        
        if (calculatedAmount > 0) {
          if (allValues.amountType === 'debit') {
            addForm.setFieldsValue({
              debitAmount: calculatedAmount,
              creditAmount: 0
            });
          } else if (allValues.amountType === 'credit') {
            addForm.setFieldsValue({
              creditAmount: calculatedAmount,
              debitAmount: 0
            });
          }
        }
      }
    }}
  >
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12}>
        <Form.Item
          name="date"
          label={<span className="text-xs font-medium">Date</span>}
          rules={[{ required: true, message: 'Select date' }]}
        >
          <DatePicker 
            className="w-full text-xs" 
            format="DD/MM/YYYY"
            placeholder="Select date"
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="transport" label={<span className="text-xs font-medium">Transport</span>}>
          <Input placeholder="Vehicle number" className="text-xs" />
        </Form.Item>
      </Col>
    </Row>

    <Form.Item
      name="description"
      label={<span className="text-xs font-medium">Description</span>}
      rules={[{ required: true, message: 'Enter description' }]}
    >
      <TextArea 
        rows={2} 
        placeholder="Transaction description"
        maxLength={200}
        showCount
        className="text-xs"
      />
    </Form.Item>

    <Row gutter={[16, 0]}>
      <Col xs={12} sm={8}>
        <Form.Item name="quantity" label={<span className="text-xs font-medium">Qty</span>}>
          <Input 
            type="number" 
            placeholder="1" 
            min={0} 
            step={1} // Changed to 1 for integer stepping
            className="text-xs" 
          />
        </Form.Item>
      </Col>
      <Col xs={12} sm={8}>
        <Form.Item name="rate" label={<span className="text-xs font-medium">Rate</span>}>
          <Input 
            type="number" 
            placeholder="0" 
            min={0} 
            step={0.01} // Keep 0.01 for currency
            prefix="₹" 
            className="text-xs" 
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item name="amountType" label={<span className="text-xs font-medium">Type</span>} rules={[{ required: true }]}>
          <Select placeholder="Select" className="text-xs">
            <Option value="debit">Debit (Out)</Option>
            <Option value="credit">Credit (In)</Option>
          </Select>
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12}>
        <Form.Item name="debitAmount" label={<span className="text-xs font-medium">Debit Amount</span>}>
          <Input 
            type="number" 
            placeholder="0" 
            min={0}
            step={0.01}
            prefix="₹"
            className="text-xs"
            onChange={(e) => {
              // If user manually changes debit, clear credit and reset type
              if (e.target.value && parseFloat(e.target.value) > 0) {
                addForm.setFieldsValue({
                  creditAmount: 0,
                  amountType: 'debit'
                });
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="creditAmount" label={<span className="text-xs font-medium">Credit Amount</span>}>
          <Input 
            type="number" 
            placeholder="0" 
            min={0}
            step={0.01}
            prefix="₹"
            className="text-xs"
            onChange={(e) => {
              // If user manually changes credit, clear debit and reset type
              if (e.target.value && parseFloat(e.target.value) > 0) {
                addForm.setFieldsValue({
                  debitAmount: 0,
                  amountType: 'credit'
                });
              }
            }}
          />
        </Form.Item>
      </Col>
    </Row>

    <div className="flex justify-end gap-2 pt-4 border-t">
      <Button 
        onClick={() => {
          setAddTransactionModal(false);
          addForm.resetFields();
        }}
        size="small"
      >
        <span className="text-xs">Cancel</span>
      </Button>
      <Button type="primary" htmlType="submit" loading={loading} size="small">
        <span className="text-xs">Add Transaction</span>
      </Button>
    </div>
  </Form>
</Modal>
  
  {/* Edit Transaction Modal */}
<Modal
  title={<span className="text-sm font-semibold">Edit Transaction</span>}
  open={editTransactionModal}
  onCancel={() => {
    setEditTransactionModal(false);
    setSelectedTransaction(null);
    editForm.resetFields();
  }}
  footer={null}
  width="min(95vw, 800px)"
  centered
>
  <Form
    form={editForm}
    layout="vertical"
    onFinish={handleEditTransaction}
    className="mt-4"
    onValuesChange={(changedValues, allValues) => {
      // Auto-calculate amounts when quantity or rate changes
      if (changedValues.quantity !== undefined || changedValues.rate !== undefined) {
        const qty = parseFloat(allValues.quantity || 0);
        const rate = parseFloat(allValues.rate || 0);
        const calculatedAmount = qty * rate;
        
        if (calculatedAmount > 0) {
          // Determine which field to update based on existing values
          const currentDebit = parseFloat(allValues.debitAmount || 0);
          const currentCredit = parseFloat(allValues.creditAmount || 0);
          
          if (currentDebit > currentCredit) {
            editForm.setFieldsValue({
              debitAmount: calculatedAmount,
              creditAmount: 0
            });
          } else {
            editForm.setFieldsValue({
              creditAmount: calculatedAmount,
              debitAmount: 0
            });
          }
        }
      }
    }}
  >
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12}>
        <Form.Item
          name="date"
          label={<span className="text-xs font-medium">Date</span>}
          rules={[{ required: true, message: 'Please select date' }]}
        >
          <DatePicker 
            className="w-full text-xs" 
            format="DD/MM/YYYY"
            placeholder="Select date"
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="transport" label={<span className="text-xs font-medium">Transport (Vehicle No.)</span>}>
          <Input placeholder="Enter vehicle number" className="text-xs" />
        </Form.Item>
      </Col>
    </Row>

    <Form.Item
      name="description"
      label={<span className="text-xs font-medium">Description</span>}
      rules={[{ required: true, message: 'Please enter description' }]}
    >
      <TextArea 
        rows={2} 
        placeholder="Enter transaction description"
        maxLength={200}
        showCount
        className="text-xs"
      />
    </Form.Item>

    <Row gutter={[16, 0]}>
      <Col xs={24} sm={8}>
        <Form.Item name="quantity" label={<span className="text-xs font-medium">Quantity</span>}>
          <Input 
            type="number" 
            placeholder="Enter quantity" 
            min={0} 
            step={1} // Changed to 1 for integer stepping
            className="text-xs" 
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <Form.Item name="rate" label={<span className="text-xs font-medium">Rate</span>}>
          <Input 
            type="number" 
            placeholder="Enter rate" 
            min={0} 
            step={0.01} // Keep 0.01 for currency
            prefix="₹" 
            className="text-xs" 
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={8}>
        <div className="text-xs text-gray-600 mb-2 font-medium">Auto-calculated from Qty × Rate</div>
      </Col>
    </Row>

    <Row gutter={[16, 0]}>
      <Col xs={24} sm={12}>
        <Form.Item name="debitAmount" label={<span className="text-xs font-medium">Debit Amount (Money Out)</span>}>
          <Input 
            type="number" 
            placeholder="Enter debit amount" 
            min={0}
            step={0.01}
            prefix="₹"
            className="text-xs"
            onChange={(e) => {
              // If user manually changes debit, clear credit
              if (e.target.value && parseFloat(e.target.value) > 0) {
                editForm.setFieldsValue({ creditAmount: 0 });
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="creditAmount" label={<span className="text-xs font-medium">Credit Amount (Money In)</span>}>
          <Input 
            type="number" 
            placeholder="Enter credit amount" 
            min={0}
            step={0.01}
            prefix="₹"
            className="text-xs"
            onChange={(e) => {
              // If user manually changes credit, clear debit
              if (e.target.value && parseFloat(e.target.value) > 0) {
                editForm.setFieldsValue({ debitAmount: 0 });
              }
            }}
          />
        </Form.Item>
      </Col>
    </Row>

    <div className="flex justify-end gap-2 pt-4 border-t">
      <Button 
        onClick={() => {
          setEditTransactionModal(false);
          setSelectedTransaction(null);
          editForm.resetFields();
        }}
        size="small"
      >
        <span className="text-xs">Cancel</span>
      </Button>
      <Button type="primary" htmlType="submit" loading={loading} size="small">
        <span className="text-xs">Update Transaction</span>
      </Button>
    </div>
  </Form>
</Modal>
  
        {/* Import Excel Modal */}
        <Modal
          title={<span className="text-sm font-semibold">Import Excel</span>}
          open={importExcelModal}
          onCancel={() => {
            setImportExcelModal(false);
            importForm.resetFields();
          }}
          footer={null}
          width="min(90vw, 600px)"
          centered
        >
          <Form
            form={importForm}
            layout="vertical"
            onFinish={handleImportExcel}
            className="mt-4"
          >
            <Form.Item
              name="importMode"
              label={<span className="text-xs font-medium">Import Mode</span>}
              rules={[{ required: true }]}
            >
              <Radio.Group className="w-full">
                <div className="flex flex-col gap-2">
                  <Radio.Button value="append" className="text-xs">Append to Session</Radio.Button>
                  <Radio.Button value="replace" className="text-xs">Replace All</Radio.Button>
                </div>
              </Radio.Group>
            </Form.Item>
  
            <Form.Item
              name="file"
              label={<span className="text-xs font-medium">Excel File</span>}
              rules={[{ required: true, message: 'Upload file' }]}
            >
              <AntUpload
                beforeUpload={() => false}
                accept=".xlsx,.xls"
                maxCount={1}
              >
                <Button icon={<Upload size={14} />} className="w-full" size="small">
                  <span className="text-xs">Select Excel File</span>
                </Button>
              </AntUpload>
            </Form.Item>
  
            <div className="bg-gray-50 p-3 rounded text-xs">
              <h4 className="font-medium mb-2">Required Columns:</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                <div>• Date (DD/MM/YYYY)</div>
                <div>• Description</div>
                <div>• Quantity</div>
                <div>• Rate</div>
                <div>• Transport (optional)</div>
                <div>• Debit/Credit Amount</div>
              </div>
            </div>
  
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                onClick={() => {
                  setImportExcelModal(false);
                  importForm.resetFields();
                }}
                size="small"
              >
                <span className="text-xs">Cancel</span>
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} size="small">
                <span className="text-xs">Import</span>
              </Button>
            </div>
          </Form>
        </Modal>
      </DashboardLayout>
    );
  };
  
  export default Accounts;